import React, { useMemo } from "react";
import { useQuery } from "@apollo/client/react";
import Skeleton from "@mui/material/Skeleton";
import Autocomplete from "@mui/material/Autocomplete";
import TextField from "@mui/material/TextField";
import uniq from "lodash/uniq";
import { Field } from "formik";
import useAuth from "src/hooks/useAuth";
import { CLIENTS_AND_PROJECTS_QUERY } from "../../constants/graphql/queries";
import Checkbox from "@mui/material/Checkbox";
import CheckBoxOutlineBlankIcon from "@mui/icons-material/CheckBoxOutlineBlank";
import CheckBoxIcon from "@mui/icons-material/CheckBox";
import ListSubheader from "@mui/material/ListSubheader";

const icon = <CheckBoxOutlineBlankIcon fontSize="small" />;
const checkedIcon = <CheckBoxIcon fontSize="small" />;

export default function ClientProjectAutocomplete({
  name,
  label,
  placeholder,
  required = false,
  multiple = false,
  ...rest
}) {
  const { user } = useAuth();
  const { settings } = user.company;
  const { data: { clients = [] } = {}, loading } = useQuery(CLIENTS_AND_PROJECTS_QUERY);

  const projectsOptions = useMemo(
    () =>
      clients.flatMap(client =>
        client.projects
          .filter(project => project.active)
          .map(project => ({
            label: project.name,
            value: project.id,
            clientId: client.id,
          }))
      ),
    [clients]
  );

  if (loading) {
    return <Skeleton variant="rounded" width="100%" height={40} />;
  }

  return (
    <Field name={name}>
      {({ field, form, meta }) => {
        const errorText = (meta.touched || form.submitCount > 0) && meta.error ? meta.error : "";

        const selectedValue = multiple
          ? projectsOptions.filter(opt => (field.value || []).includes(opt.value))
          : projectsOptions.find(opt => opt.value === field.value) || null;

        const handleChange = (event, newValue, reason, details) => {
          if (multiple) {
            const currentIds = field.value || [];
            let nextIds;

            switch (reason) {
              case "selectOption": {
                const idToAdd = details?.option?.value;
                nextIds = uniq([...currentIds, idToAdd]);
                break;
              }
              case "removeOption": {
                const idToRemove = details?.option?.value;
                nextIds = currentIds.filter(id => id !== idToRemove);
                break;
              }
              case "clear": {
                nextIds = [];
                break;
              }
              default: {
                nextIds = uniq((newValue || []).map(opt => opt.value));
              }
            }

            form.setFieldValue(field.name, nextIds);
            if (rest.onChange) rest.onChange(nextIds);
          } else {
            form.setFieldValue(field.name, newValue ? newValue.value : "");
            if (rest.onChange) rest.onChange(newValue);
          }
        };

        return (
          <Autocomplete
            multiple={multiple}
            disableCloseOnSelect={multiple}
            {...rest}
            options={projectsOptions}
            groupBy={option => option.clientId}
            getOptionLabel={option => option.label}
            isOptionEqualToValue={(option, value) => option?.value === value?.value}
            value={selectedValue}
            onChange={handleChange}
            renderGroup={params => {
              const clientId = params.group;
              const client = clients.find(c => c.id === clientId);
              const clientName = client ? client.name : clientId;

              const groupProjectIds = uniq(
                projectsOptions.filter(opt => opt.clientId === clientId).map(opt => opt.value)
              );
              const selectedIds = field.value || [];
              const selectedCount = selectedIds.filter(id => groupProjectIds.includes(id)).length;
              const allSelected =
                selectedCount === groupProjectIds.length && groupProjectIds.length > 0;
              const indeterminate = selectedCount > 0 && !allSelected;

              const handleGroupCheckboxChange = event => {
                event.stopPropagation();
                let newValues;
                if (allSelected) {
                  newValues = selectedIds.filter(id => !groupProjectIds.includes(id));
                } else {
                  newValues = Array.from(new Set([...selectedIds, ...groupProjectIds]));
                }
                form.setFieldValue(field.name, newValues);
                if (rest.onChange) rest.onChange(newValues);
              };

              return [
                <ListSubheader
                  key={`${params.key}-subheader`}
                  component="div"
                  style={{ lineHeight: 1, backgroundColor: "#f4f6f8" }}
                >
                  {multiple && (
                    <Checkbox
                      icon={icon}
                      checkedIcon={checkedIcon}
                      indeterminate={indeterminate}
                      checked={allSelected}
                      onClick={handleGroupCheckboxChange}
                      style={{ marginRight: 8 }}
                    />
                  )}
                  {clientName}
                </ListSubheader>,
                params.children,
              ];
            }}
            renderOption={(props, option, { selected }) => (
              <li {...props} style={{ paddingLeft: 8 }}>
                {multiple && (
                  <Checkbox
                    icon={icon}
                    checkedIcon={checkedIcon}
                    style={{ marginRight: 8, marginLeft: 24 }}
                    checked={selected}
                  />
                )}
                {option.label}
              </li>
            )}
            renderTags={value => (
              <span
                style={{
                  whiteSpace: "nowrap",
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                  display: "block",
                  padding: "0 8px",
                }}
                title={value.map(opt => opt.label).join(", ")}
              >
                {value.map(opt => opt.label).join(", ")}
              </span>
            )}
            renderInput={params => (
              <TextField
                {...params}
                label={label || settings.filter_field2 || "Project"}
                placeholder={placeholder || `Select ${settings.filter_field2}`}
                variant="outlined"
                size="small"
                required={required}
                error={!!errorText}
                helperText={errorText}
              />
            )}
          />
        );
      }}
    </Field>
  );
}
